import { Link } from "@tanstack/react-router";

import { useCurrentUser } from "@/auth/useCurrentUser";
import { TeamInviteForm } from "@/components/TeamInviteForm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * /dashboard/partner/team — a PO invites team members under their own
 * organisation. CHP-295: the form is the shared <TeamInviteForm> (per-row
 * email + access level), the same flow embedded in the onboarding
 * Registration Complete page per the design review.
 */
export function PartnerTeamRoute() {
  const me = useCurrentUser();

  if (me.isAuthLoading || me.isLoading) {
    return <p role="status">Loading…</p>;
  }
  if (!me.isAuthenticated || !me.data || !me.accessToken) {
    return <p role="status">Sign in to manage your team.</p>;
  }
  // CHP-295: only the org's PO may invite (the form can mint peer POs).
  // BDMs who deep-link here get a plain explanation, not a 403 surprise.
  // CHP-805: steady-state informational guard — role="status" (polite), not
  // role="alert" (an assertive interruption suits transient errors, not this).
  //
  // CHP-808: the PO check mirrors the backend (partners.py): a channel_partner
  // is a PO unless is_org_admin === false (back-compat: null/true → PO). It is
  // NOT `!is_org_admin`, because a legitimate PO can carry is_org_admin null.
  // The admin case (is_org_admin null because admins have no PartnerProfile) is
  // handled by the route gate, which now drops `admin` entirely — so we also
  // require the role to be channel_partner here as defence-in-depth: any
  // non-partner who somehow reaches this component sees the explanation, never
  // a form that POST /partners/me/team-invites would 403.
  const isProgramOwner = me.data.role === "channel_partner" && me.data.is_org_admin !== false;
  if (!isProgramOwner) {
    return (
      <p role="status" className="p-6 text-sm text-muted-foreground">
        Only your organisation&apos;s Program Owner can invite team members.
      </p>
    );
  }

  const orgName = me.data.organization?.company_name ?? null;
  const orgId = me.data.organization?.id ?? null;

  return (
    <div className="container max-w-2xl py-6 space-y-6">
      <div>
        <Link to="/dashboard/partner" className="text-sm text-muted-foreground hover:underline">
          ← Dashboard
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">Invite your team</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Team members</CardTitle>
        </CardHeader>
        <CardContent>
          <TeamInviteForm token={me.accessToken} orgName={orgName} orgId={orgId} />
        </CardContent>
      </Card>
    </div>
  );
}
